import { useMemo } from "preact/hooks";
import type { DerivedSheet, ItemSummary } from "../engine";
import { useSheetActions } from "./sheet-context";
import { ListRow } from "./tiles";

interface Props {
  sheet: DerivedSheet;
}

const PHYSICAL = new Set(["weapon", "armor", "shield", "equipment", "consumable", "ammo", "backpack", "treasure", "book", "kit"]);
const FOUNDATIONS = new Set(["ancestry", "heritage", "background", "class"]);
const TYPE_LABELS: Record<string, string> = { feat: "Feat", action: "Action", ancestry: "Ancestry", heritage: "Heritage", background: "Background", class: "Class", deity: "Deity", spell: "Spell", condition: "Condition", effect: "Effect", campaignFeature: "Campaign" };

/**
 * The Feats and Features tab (issue #14): every non-physical Entry the Character holds, grouped by
 * the Level that opened it, with who granted it, its alterations and any open answer; tap a name
 * to read it in the viewer. Conditions and Effects in play stay on the Session tab.
 */
export function Feats({ sheet }: Props) {
  const { openEntry } = useSheetActions();
  const groups = useMemo(() => {
    const byLevel = new Map<number | "other", ItemSummary[]>();
    for (const item of sheet.items) {
      if (PHYSICAL.has(item.type) || item.sessionId !== null) continue;
      if (item.inMemory && (item.type === "condition" || item.type === "effect")) continue;
      const key = item.level ?? "other";
      byLevel.set(key, [...(byLevel.get(key) ?? []), item]);
    }
    const levels = [...byLevel.keys()].filter((k): k is number => typeof k === "number").sort((a, b) => a - b);
    return [...levels.map((l) => ({ key: String(l), title: `Level ${l}`, items: byLevel.get(l)! })), ...(byLevel.has("other") ? [{ key: "other", title: "Held outside the progression", items: byLevel.get("other")! }] : [])];
  }, [sheet.items]);
  const foundations = sheet.items.filter((i) => FOUNDATIONS.has(i.type));

  return (
    <div class="feats">
      {foundations.length > 0 && (
        <section class="card list">
          <h3>Foundations</h3>
          {foundations.map((i) => <Row key={`${i.choiceKey}:${i.uuid}`} item={i} open={openEntry} />)}
        </section>
      )}
      {groups.map((g) => (
        <section key={g.key} class="card list">
          <h3>{g.title}</h3>
          {g.items.filter((i) => !FOUNDATIONS.has(i.type)).map((i) => <Row key={`${i.choiceKey}:${i.uuid}:${i.grantedBy?.uuid ?? ""}`} item={i} open={openEntry} />)}
        </section>
      ))}
      {sheet.items.length === 0 && <p class="muted">Nothing yet: pick an ancestry, a background and a class in the Build tab.</p>}
    </div>
  );
}

function Row({ item, open }: { item: ItemSummary; open: (uuid: string) => void }) {
  return (
    <ListRow onTap={() => open(item.uuid)} class={item.active ? "" : "inactive"}>
      <span class="row-main">
        <span class="row-name">{item.name}</span>
        <span class="tag">{TYPE_LABELS[item.type] ?? item.type}</span>
        {item.inMemory ? <span class="tag">in memory</span> : null}
        {item.openPrompts.length > 0 ? <span class="tag open">open: {item.openPrompts.join(", ")}</span> : null}
        {(item.grantedBy || item.alterations.length > 0) && (
          <span class="row-sub muted">
            {item.grantedBy ? `granted by ${item.grantedBy.name}` : ""}
            {item.grantedBy && item.alterations.length > 0 ? " · " : ""}
            {item.alterations.length > 0 ? `altered: ${item.alterations.join("; ")}` : ""}
          </span>
        )}
      </span>
      <span class="row-value muted">›</span>
    </ListRow>
  );
}
